import React, {ReactNode, useEffect, useState} from 'react';
import {Navigate, Outlet, useOutlet} from 'react-router-dom';
import {useAuth} from '../contexts/AuthContext';
import {assessmentService} from '../services/assessmentService';

interface ProtectedRouteProps {
    children?: ReactNode;
    requiredPermission?: string;
}

const TOKEN_KEY = 'auth_token';

/**
 * 路由守卫组件
 * - 检查用户是否已登录（从AuthContext获取isAuthenticated）
 * - 如果未登录，跳转到登录页面 /login
 * - 如果已登录但未完成测评，跳转到 /assessment
 * - 如果已登录且已完成测评，渲染子组件（children）或Outlet（嵌套路由）
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({children, requiredPermission}) => {
    const {isAuthenticated, isLoading, user, hasPermission} = useAuth();
    const outlet = useOutlet();

    // 立即检查 localStorage 中的 token，不依赖 AuthContext 初始化
    const [hasLocalToken] = useState(() => {
        try {
            return !!localStorage.getItem(TOKEN_KEY);
        } catch {
            return false;
        }
    });

    // 测评状态：null表示未检查，true表示已检查且完成，false表示已检查但未完成
    const [assessmentCompleted, setAssessmentCompleted] = useState<boolean | null>(null);
    // 服务端失联标记：网络错误（断网/超时/服务端不可达）时置为 true，跳转登录页
    const [connectionLost, setConnectionLost] = useState(false);

    // 检查测评状态 - 必须在所有条件检查之前调用
    useEffect(() => {
        if (user?.id && assessmentCompleted === null) {
            assessmentService.getAssessmentStatus(user.id)
                .then(response => {
                    setAssessmentCompleted(response.completed);
                })
                .catch((error: any) => {
                    // 服务端失联（断网/超时/后端不可达，api.ts 已标记 __networkError）→ 跳登录页
                    if (error?.__networkError) {
                        setConnectionLost(true);
                        return;
                    }
                    // 业务失败（服务端正常响应但接口异常）：默认未完成测评，让用户去做测评
                    // 如果用户已做过测评，提交时会提示已存在
                    setAssessmentCompleted(false);
                });
        }
    }, [user?.id, assessmentCompleted]);

    // 如果 AuthContext 还在加载，但 localStorage 没有 token，立即重定向
    if (isLoading && !hasLocalToken) {
        return <Navigate to="/login" replace/>;
    }

    // 加载中时不做跳转，等AuthContext初始化完成
    if (isLoading) {
        return null;
    }

    // 未登录，跳转到登录页面
    if (!isAuthenticated) {
        return <Navigate to="/login" replace/>;
    }

    // 服务端失联（断网/超时/不可达），跳转到登录页面
    if (connectionLost) {
        return <Navigate to="/login" replace/>;
    }

    // 还在检查测评状态
    if (assessmentCompleted === null) {
        return null;
    }

    // 已登录但未完成测评，跳转到测评页面
    if (!assessmentCompleted) {
        return <Navigate to="/assessment" replace/>;
    }

    // 权限检查：如果指定了requiredPermission但用户没有该权限，显示无权限提示
    if (requiredPermission && !hasPermission(requiredPermission)) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                color: 'var(--pt-text-secondary)'
            }}>
                <div style={{fontSize: '48px', marginBottom: '16px', color: 'var(--pt-bear)'}}>403</div>
                <div style={{fontSize: '18px', marginBottom: '8px', color: 'var(--pt-text-primary)'}}>暂无权限访问</div>
                <div style={{fontSize: '14px'}}>请联系管理员获取相应权限</div>
            </div>
        );
    }

    // 如果有嵌套路由的Outlet（通过useOutlet获取），优先使用Outlet
    // 否则渲染children（直接传递的页面组件）
    return outlet ? <Outlet/> : <>{children}</>;
};

export default ProtectedRoute;
